"use client";

import Link from "next/link";
import { type ReactNode, useRef } from "react";

import { DotFieldBackdrop } from "@/components/auth/hero-dot-field/dot-field-backdrop";
import { TeracWordmark } from "@/components/icons/TeracWordmark";
import type { Audience } from "@/lib/audience-preference";

/**
 * The hero panel for one side of the product, shared by both steps of the
 * sign-in flow.
 *
 * Step 1 (the audience gate) previews this panel as you pick a side and step 2
 * (the sign-in itself) keeps showing it, so the two steps have to agree. They
 * were defined twice before and drifted: the researcher panel went white on
 * step 2 while step 1 still showed it near-black.
 */
export interface AudienceHero {
  /** Which side this panel is, which selects its dot field and its marks. */
  audience: Audience;
  /** Two lines, as set in the design. */
  headline: ReactNode;
  subtitle: string;
  /** Panel background. Both sides are dark surfaces, so the ink stays white. */
  panelClass: string;
}

export const AUDIENCE_HERO: Record<Audience, AudienceHero> = {
  expert: {
    audience: "expert",
    headline: (
      <>
        Get paid for
        <br />
        your expertise.
      </>
    ),
    subtitle: "Join experts worldwide on paid research opportunities.",
    panelClass: "bg-marketing-blue",
  },
  researcher: {
    audience: "researcher",
    headline: (
      <>
        Run research with
        <br />
        verified experts.
      </>
    ),
    subtitle:
      "Recruit niche audiences, find experts and pay only for verified completions.",
    // Near-black in light mode, which is what tells the two sides apart at a
    // glance. Dark mode lifts the panel off the page and adds a hairline:
    // #0f0f0e on the #0a0a0a page measured 1.03:1 and the panel vanished.
    panelClass:
      "bg-[#0f0f0e] dark:border dark:border-[#2a2a31] dark:bg-[#16161a]",
  },
};

/**
 * The shipped ramp, for a panel that is half the viewport. All arbitrary
 * min-width variants on purpose: mixing them with named breakpoints silently
 * broke this once, because Tailwind emitted `xl` after `min-[1440px]` and the
 * 64px step became dead CSS. Each step is the largest size that still keeps
 * the longest line ("Run research with", the wider of the two sides) on one
 * line at that width, so both sides hold the designed two-line rag. 76px only
 * starts at 1536 because at 1440 it measures 600px against 592px of panel.
 */
const DEFAULT_HEADLINE_RAMP =
  "text-[40px] min-[1024px]:text-[44px] min-[1280px]:text-[60px] min-[1440px]:text-[72px] min-[1536px]:text-[76px]";

/**
 * The left-hand hero panel, rendered identically by step 1 and step 2.
 *
 * One component rather than a copy per step: the markup was duplicated before
 * and the two copies disagreed about the panel colour. Sharing it also keeps
 * the type ramp and the headline's vertical position the same on both sides,
 * which is the thing a visitor notices when the gate advances to the sign-in.
 *
 * Hidden below `lg`, where the form column stands alone.
 */
export function AuthHeroPanel({
  audience,
  headline,
  subtitle,
  panelClass,
  headlineClassName = DEFAULT_HEADLINE_RAMP,
  marks = true,
}: AudienceHero & {
  /**
   * Overrides the type ramp below. That ramp is keyed to the viewport because
   * it assumes this panel is exactly half of it; a caller that gives the panel
   * a different share has to size the headline against its own width instead,
   * or the longest line wraps and the marks reseat around the overflow.
   */
  headlineClassName?: string;
  /** Forwarded to the backdrop; see its `marks` prop. */
  marks?: boolean;
}) {
  const wordmarkRef = useRef<HTMLAnchorElement | null>(null);
  const headlineRef = useRef<HTMLParagraphElement | null>(null);
  const subtitleRef = useRef<HTMLParagraphElement | null>(null);

  return (
    <div className="hidden p-2 lg:flex">
      <div
        // 8px corners, matching the panel on the live /login page. The colour
        // transitions because step 1 swaps it as you pick a side.
        className={`relative flex w-full flex-col overflow-hidden rounded-lg px-14 py-12 text-white transition-colors duration-300 ${panelClass}`}
      >
        {/* Behind the copy, and inert. The marks measure the rendered ink of
            the headline and subtitle and seat themselves clear of it, so they
            follow the type ramp instead of assuming the comp's sizes. */}
        <DotFieldBackdrop
          audience={audience}
          headlineRef={headlineRef}
          subtitleRef={subtitleRef}
          wordmarkRef={wordmarkRef}
          marks={marks}
        />

        <Link
          href="/"
          aria-label="Terac home"
          ref={wordmarkRef}
          className="relative"
        >
          <TeracWordmark className="h-10 w-auto text-white" />
        </Link>

        {/* A fixed band rather than a flex ratio. With a ratio, the leftover
            space was split around the text block, so the researcher's
            two-line subtitle pushed its headline higher than the expert's and
            the headline jumped as you switched sides. Anchoring the headline
            from the top and the subtitle to the bottom makes both independent
            of how many lines the copy takes, so the two sides line up exactly.
            14.2% puts the headline's top at y=200 on an 884px hero, which is
            where node 4890:342 has it, and `mt-6` on the subtitle is the 24px
            gap that design leaves under the headline. The number is the 112px
            the design leaves between the wordmark and the headline over the
            788px content box, so it holds at any panel height. It was briefly
            20%, which read better against the centred form column but no
            longer matched the design. The form is centred on the screen and
            this is anchored from the top, so the two are positioned
            differently on purpose and the headings do not line up. */}
        <span aria-hidden className="h-[14.2%] shrink-0" />

        {/* Decorative: the page's h1 is the heading in the form column. See
            DEFAULT_HEADLINE_RAMP for why the sizes are what they are. */}
        <p
          ref={headlineRef}
          className={`relative m-0 text-left leading-none font-medium tracking-[-0.04em] text-white ${headlineClassName}`}
        >
          {headline}
        </p>

        {/* Directly under the headline, as the design has it, rather than
            pushed to the foot of the panel: the dot field behind it is the
            subject of the lower half now.

            470 not 430: the browser sets Geist a shade wider than the comp
            measures it, and at 430 the expert's one-line subtitle wrapped. The
            researcher's is long enough to take two lines either way. */}
        <p
          ref={subtitleRef}
          className="relative m-0 mt-6 max-w-[470px] text-left text-[17px] leading-[1.55] text-white/72"
        >
          {subtitle}
        </p>
      </div>
    </div>
  );
}
